import React from "react";
import { MdOutlineWarningAmber, MdErrorOutline } from "react-icons/md";
import { TbAlertCircle } from "react-icons/tb";

import { AlertContentProps } from "./types";
import { UserStatus } from "@/types/user";
import { UserContextProps } from "@/contexts/UserContext/types";
import { SubscriptionStatus } from "@/types/subscription";
import { Typography } from "../Typography";
import { getIsQRCreator } from "@/utils/site";
import { getAlertContent } from "./utils";
import {
  isUserFreeTrial,
  isUserNotAuthorized,
  isUserTrialExpired,
} from "@/services/UserService/UserService";
import { Link } from "../Link";
import { Alert } from "./Alert";

const isAccountValidatedAndActived = (userContext: UserContextProps, isUserValidated: boolean) => {
  let isActive = false;

  if (
    !userContext?.user?.email ||
    !isUserValidated ||
    userContext?.user?.subscription?.status === SubscriptionStatus.ACTIVE
  ) {
    isActive = true;
  }

  return isActive;
};

const shouldShowAlert = (userContext: UserContextProps, isUserValidated: boolean): boolean => {
  const { user } = userContext || {};
  const { email, subscription } = user || {};
  const hasActiveSubscription = subscription?.status === SubscriptionStatus.ACTIVE;

  return Boolean(email && isUserValidated && !hasActiveSubscription);
};

export const DashboardAlert = ({
  isUserValidated,
  userContext,
  className,
  content,
}: AlertContentProps) => {
  const userTrialActive = isUserFreeTrial(userContext) || isUserNotAuthorized(userContext);
  const userIsTrialExpired = isUserTrialExpired(userContext);

  // Special case for qrcreator.com
  const isQRCreatorDomain = getIsQRCreator();
  const userIsRegistered =
    isQRCreatorDomain &&
    userContext?.user?.userStatus?.toLowerCase() === UserStatus.REGISTERED.toLowerCase();
  const shouldShowTextAndLink = userTrialActive || userIsRegistered;

  if (isAccountValidatedAndActived(userContext, isUserValidated)) return;

  if (!shouldShowAlert(userContext, isUserValidated)) {
    return null;
  }

  if (userIsTrialExpired) {
    const textAndButton = (
      <div className={className}>
        <div className="Alert__message">
          <MdErrorOutline aria-label="alert-icon" />
          <Typography
            Tag="p"
            classname="Alert__text"
          >
            {content.dashboardAlertTextTrialExpired}
          </Typography>
        </div>
        <Link
          dataQA="upgrade-now-button-link"
          className={"Alert__btn" + (content.ctaStyle ? " rounded" : "")}
          href={"/pricing"}
        >
          {content.dashboardAlertBtnLabelTrialExpired}
        </Link>
      </div>
    );

    return (
      <Alert
        text={textAndButton}
        style={{ padding: "12px 16px" }}
        type="error"
      />
    );
  }

  // QRCreator & QRNow
  if (shouldShowTextAndLink) {
    const textAndLink = (
      <div className={className}>
        <Typography
          Tag="p"
          classname="Alert__text"
        >
          {`${getAlertContent(content, userContext)} `}
          <Link
            dataQA="upgrade-now-button-link"
            underlined
            className="Alert__link"
            href={"/pricing"}
          >
            {content.dashboardAlertTextTrialActive2_1 || content.dashboardAlertBtnLabel}
          </Link>
        </Typography>
      </div>
    );

    return (
      <Alert
        icon={<MdOutlineWarningAmber aria-label="alert-icon" />}
        text={textAndLink}
        type="warning"
        closable
      />
    );
  }

  // Other domains
  const renderAlertText = (
    <div className={className}>
      <div className="Alert__message">
        <TbAlertCircle aria-label="alert-icon" />
        <Typography
          Tag="p"
          classname="Alert__text"
        >
          {`${getAlertContent(content, userContext)}`}
        </Typography>
      </div>
      <Link
        dataQA="upgrade-now-button-link"
        className={"Alert__btn" + (content.ctaStyle ? " rounded" : "")}
        href={"/pricing"}
      >
        {content.dashboardAlertTextTrialActive2_1 || content.dashboardAlertBtnLabel}
      </Link>
    </div>
  );

  return (
    <Alert
      text={renderAlertText}
      type={isUserNotAuthorized(userContext) ? "error" : "warning"}
    />
  );
};
